import { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Paper, Typography, TextField, IconButton, Divider } from '@mui/material';
import { Send } from '@mui/icons-material';
import api from '../../api/client';
import useCADWebSocket from '../../hooks/useCADWebSocket';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateShort, formatTime24, ensureTimezone } from '../../utils/time';

interface ChatMsg {
  id: number;
  created_at: string;
  user_id: number | null;
  username: string;
  full_name: string;
  position_name: string;
  body: string;
}

export default function ChatPage() {
  const { user } = useAuth();
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [draft, setDraft] = useState('');
  const listRef = useRef<HTMLDivElement | null>(null);
  const positionName = localStorage.getItem('operator_position_name') || '';

  const scrollToBottom = () => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  const loadHistory = useCallback(() => {
    api.get('/chat/messages', { params: { limit: 200 } })
      .then((r) => { setMessages(r.data); setTimeout(scrollToBottom, 50); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    ensureTimezone().finally(loadHistory);
  }, [loadHistory]);

  const appendMessage = useCallback((msg: ChatMsg) => {
    setMessages((prev) => {
      if (prev.some((m) => m.id === msg.id)) return prev;
      return [...prev, msg];
    });
    setTimeout(scrollToBottom, 50);
  }, []);

  const handleRawEvent = useCallback((event: string, data: any) => {
    if (event === 'chat_message' && data) appendMessage(data as ChatMsg);
  }, [appendMessage]);

  const noop = useCallback(() => {}, []);
  useCADWebSocket(noop, 'dispatch', undefined, handleRawEvent);

  const send = async () => {
    const body = draft.trim();
    if (!body) return;
    setDraft('');
    try {
      // Show the sent message immediately; the broadcast is deduped by id.
      const r = await api.post('/chat/messages', { body, position_name: positionName });
      if (r.data) appendMessage(r.data as ChatMsg);
    } catch {
      setDraft(body);
    }
  };

  const senderLabel = (m: ChatMsg) => {
    const name = m.full_name || m.username || 'Unknown';
    return m.position_name ? `${m.position_name} / ${name}` : name;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 80px)' }}>
      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ p: 1, bgcolor: '#1a1a2e', color: '#fff' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Dispatch Chat</Typography>
        </Box>
        <Box ref={listRef} sx={{ flex: 1, overflow: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {messages.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
              No messages yet. Start the conversation.
            </Typography>
          )}
          {messages.map((m) => {
            const mine = user?.username && m.username === user.username;
            return (
              <Box
                key={m.id}
                sx={{
                  alignSelf: mine ? 'flex-end' : 'flex-start',
                  maxWidth: '78%',
                  bgcolor: mine ? 'primary.main' : 'action.hover',
                  color: mine ? 'primary.contrastText' : 'text.primary',
                  borderRadius: 1.5,
                  px: 1.25,
                  py: 0.75,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.25 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.9 }}>{senderLabel(m)}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    {m.created_at ? `${formatDateShort(m.created_at)} ${formatTime24(m.created_at)}` : ''}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.body}</Typography>
              </Box>
            );
          })}
        </Box>
        <Divider />
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, p: 1 }}>
          <TextField
            fullWidth
            size="small"
            multiline
            maxRows={4}
            placeholder="Type a message…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            autoFocus
          />
          <IconButton color="primary" onClick={send} disabled={!draft.trim()}>
            <Send />
          </IconButton>
        </Box>
      </Paper>
    </Box>
  );
}
